<script lang="ts">
  import { createEventDispatcher, tick } from 'svelte';
  // @ts-ignore
  import Sortable from 'sortablejs';
  // @ts-ignore
  import { Button } from '$lib/components/ui/button';
  import Icon from '$shared/icons/Icon.svelte';
  import EditableTextArea from '../shared/EditableTextArea.svelte';
  import QuestionSetDurationField from './QuestionSetDurationField.svelte';
  import AddQuestionMenu from './AddQuestionMenu.svelte';
  import QuestionCard from './QuestionCard.svelte';
  import QuestionSetCard from './QuestionSetCard.svelte';
  import MoveMenu from './MoveMenu.svelte';
  import { sortableList } from './sortableList';
  import type { QuestionSetQuestion } from './curationTypes';

  // The curated item id, used by the list to keep its order.
  export let id: number;
  export let curationId: number;
  export let questionSetId: number;
  export let editable = false;
  export let open = false;
  export let responseCount = 0;
  export let questionCount = 0;
  export let title: string | null = '';
  export let description: string | null = '';
  export let durationSeconds: number | null = null;
  export let initialQuestions: QuestionSetQuestion[] = [];
  let questions = initialQuestions;

  export let titlePlaceholder = 'Click to edit question set title';
  export let descriptionPlaceholder = 'Click to edit question set description';
  export let durationLabel = 'Duration';
  export let durationPlaceholder = 'Click to set duration';
  export let minutesText = 'minutes';
  export let emptyStateText = 'Start building your question set by adding a question.';
  export let addQuestionText = 'Add question';
  export let bestPracticesText = 'See best practices for writing questions in our';
  export let helpCenterLinkText = 'help center';
  export let saveAndCloseText = 'Save and close';
  export let moveLabel = 'Move question set';
  export let duplicateLabel = 'Duplicate question set';
  export let deleteLabel = 'Delete question set';
  export let multipleChoiceText = 'Multiple choice';
  export let openEndedText = 'Open-ended';
  export let questionPlaceholder = 'Enter your question here...';
  export let responsePreviewText = 'Learners will type their response here.';
  export let optionPlaceholder = 'Enter option here...';
  export let addOptionText = 'Add option';
  export let deleteOptionLabel = 'Delete option';
  export let deleteOptionConfirmText = 'Are you sure you want to delete this option?';
  export let deleteText = 'Delete';
  export let cancelText = 'Cancel';
  export let moveQuestionLabel = 'Move question';
  export let moveToTopLabel = 'Move to top';
  export let moveUpLabel = 'Move up';
  export let moveDownLabel = 'Move down';
  export let moveToBottomLabel = 'Move to bottom';
  export let duplicateQuestionLabel = 'Duplicate question';
  export let deleteQuestionLabel = 'Delete question';
  export let typeLabel = 'Question set';
  export let untitledText = 'Untitled question set';
  export let questionText = 'question';
  export let questionsText = 'questions';
  export let responseText = 'response';
  export let responsesText = 'responses';
  export let editLabel = 'Edit question set';

  // Shows the curation's new total time in the page header after the set's time saves.
  function showCurationTotal(total: string) {
    const element = document.getElementById('curation-duration');
    if (element) element.textContent = total;
  }

  // Shows the section's new total time in its header, when the set sits in a section.
  function showSectionTotal(total: string | null) {
    const element = root.closest('[data-level="1"]')?.querySelector('[data-section-duration]');
    if (element && total != null) element.textContent = total;
  }

  const helpCenterUrl = 'https://help.bigthink.com/help/question-sets-to-support-learners';
  const saveUrl = `/curations/${curationId}/question_sets/${questionSetId}`;

  // Sends "move" after the set moves from its menu, so the page can refresh the sections' menus.
  const dispatch = createEventDispatcher();

  let root: HTMLDivElement;
  let isFirstItem = false;
  let isLastItem = false;

  // Works out whether the set is first or last in its list (the curation or a section), for the move menu.
  function checkPlace() {
    const order = Sortable.get(root.parentElement).toArray();
    const index = order.indexOf(String(id));
    isFirstItem = index === 0;
    isLastItem = index === order.length - 1;
  }

  // Moves the set to the top, up one, down one or to the bottom of its list, then saves its new place.
  function moveSet(direction: string) {
    const list = root.parentElement as HTMLElement;
    const sortable = Sortable.get(list);
    const order = sortable.toArray();
    const from = order.indexOf(String(id));
    const to = { top: 0, up: from - 1, down: from + 1, bottom: order.length - 1 }[direction] as number;
    order.splice(to, 0, ...order.splice(from, 1));
    sortable.sort(order, true);

    // A section's list carries its own curation id; the top level list carries the curation's.
    const listCurationId = list.dataset.curatableId ?? list.dataset.id;
    fetch(`/curations/${listCurationId}/curated_items/${id}`, {
      method: 'PATCH',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
      },
      body: JSON.stringify({ position: to, to_curation: listCurationId }),
    });
    dispatch('move');
  }

  let questionList: HTMLDivElement;
  let addingQuestion = false;

  // Creates a question of the chosen type, adds it to the end of the set and scrolls to it.
  // Clicks while a question is being added are ignored, so a double click adds only one.
  async function addQuestion(event: CustomEvent<string>) {
    if (addingQuestion) return;
    addingQuestion = true;

    try {
      const response = await fetch(`${saveUrl}/questions`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
        },
        body: JSON.stringify({ question: { question_type: event.detail } }),
      });
      if (!response.ok) return;

      const data = await response.json();
      questions = [...questions, data.question];
      await tick();
      questionList?.lastElementChild?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } finally {
      addingQuestion = false;
    }
  }

  // Moves a question to the top, up one, down one or to the bottom, from its move menu.
  function moveQuestion(index: number, direction: string) {
    const target = { top: 0, up: index - 1, down: index + 1, bottom: questions.length - 1 }[direction];
    moveQuestionTo(index, target);
  }

  // Moves a question from one place to another, then saves its new place.
  function moveQuestionTo(from: number, to: number) {
    const reordered = [...questions];
    const [moved] = reordered.splice(from, 1);
    reordered.splice(to, 0, moved);
    questions = reordered;
    saveQuestionPosition(moved.id, to + 1);
  }

  // Saves a question's place in the set (1 is the top).
  function saveQuestionPosition(questionId: number, position: number) {
    fetch(`${saveUrl}/questions/${questionId}`, {
      method: 'PATCH',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
      },
      body: JSON.stringify({ question: { position } }),
    });
  }
</script>

<!-- Duplicate and delete are shown but don't do anything yet. -->
<div bind:this={root} data-id={id} class={open ? 'relative mx-5 flex flex-col gap-4 p-6 rounded-lg border border-neutral-200 bg-neutral-50' : 'relative'}>
  {#if editable}
    <div class="handle cursor-grab absolute w-6 h-6 grid place-content-center {open ? 'top-2 -left-8' : 'top-0 -left-3 z-10'}" aria-hidden="true">
      <Icon icon="GripVertical" size="medium" />
    </div>
  {/if}

  {#if open}
  <div class="pb-4">
    <div class="flex items-start justify-between gap-4 pb-2 border-b border-neutral-200">
      <div class="flex flex-col gap-2 flex-1 min-w-0">
        <div class="max-w-[528px] text-lg font-medium text-black">
          <EditableTextArea
            action={saveUrl}
            contents={title ?? ''}
            {editable}
            model="question_set"
            property="title"
            maxlength={255}
            placeholder={titlePlaceholder}
            on:change={(event) => (title = event.detail.contents)}
          />
        </div>
        <div class="text-base text-black">
          <EditableTextArea
            action={saveUrl}
            contents={description ?? ''}
            {editable}
            model="question_set"
            property="description"
            placeholder={descriptionPlaceholder}
            on:change={(event) => (description = event.detail.contents)}
          />
        </div>
        <div class="text-base text-black">
          <QuestionSetDurationField
            action={saveUrl}
            bind:durationSeconds
            {editable}
            label={durationLabel}
            placeholder={durationPlaceholder}
            {minutesText}
            on:saved={(event) => {
              showCurationTotal(event.detail.curation_duration);
              showSectionTotal(event.detail.section_duration);
            }}
          />
        </div>
      </div>

      <div class="flex items-center shrink-0">
        <MoveMenu
          label={moveLabel}
          isFirst={isFirstItem}
          isLast={isLastItem}
          moveTopLabel={moveToTopLabel}
          {moveUpLabel}
          {moveDownLabel}
          moveBottomLabel={moveToBottomLabel}
          on:opening={checkPlace}
          on:move={(event) => moveSet(event.detail)}
        />
        <Button variant="ghost" size="icon" class="w-8 h-8 p-0">
          <span class="sr-only">{duplicateLabel}</span>
          <Icon icon="Copy" size="small" />
        </Button>
        <Button variant="ghost" size="icon" class="w-8 h-8 p-0">
          <span class="sr-only">{deleteLabel}</span>
          <Icon icon="Trash2" size="small" />
        </Button>
      </div>
    </div>
  </div>

  {#if questions.length === 0}
    <div class="flex flex-col items-center justify-center gap-6 py-6 px-2.5 rounded-lg border border-dashed border-neutral-400 bg-white">
      <p class="text-base text-neutral-600 text-center">{emptyStateText}</p>
      <AddQuestionMenu {addQuestionText} {multipleChoiceText} {openEndedText} on:select={addQuestion} />
    </div>
  {:else}
    <div bind:this={questionList} class="flex flex-col gap-4" use:sortableList={{ handle: '.question-handle', onMove: moveQuestionTo }}>
      {#each questions as question, index (question.id)}
        <QuestionCard
          {question}
          url={`${saveUrl}/questions/${question.id}`}
          isFirst={index === 0}
          isLast={index === questions.length - 1}
          {multipleChoiceText}
          {openEndedText}
          {questionPlaceholder}
          {responsePreviewText}
          {optionPlaceholder}
          {addOptionText}
          {deleteOptionLabel}
          {deleteOptionConfirmText}
          {deleteText}
          {cancelText}
          moveLabel={moveQuestionLabel}
          moveTopLabel={moveToTopLabel}
          {moveUpLabel}
          {moveDownLabel}
          moveBottomLabel={moveToBottomLabel}
          duplicateLabel={duplicateQuestionLabel}
          deleteLabel={deleteQuestionLabel}
          on:move={(event) => moveQuestion(index, event.detail)}
        />
      {/each}
    </div>
    <div>
      <AddQuestionMenu {addQuestionText} {multipleChoiceText} {openEndedText} on:select={addQuestion} />
    </div>
  {/if}

  <div class="flex items-end justify-end gap-2.5">
    <p class="flex-1 text-base text-neutral-600">
      {bestPracticesText}
      <a href={helpCenterUrl} target="_blank" rel="noopener noreferrer" class="underline">{helpCenterLinkText}</a>.
    </p>
    <Button class="h-10 font-medium bg-black text-white border hover:bg-white hover:text-black" on:click={() => (open = false)}>{saveAndCloseText}</Button>
  </div>
  {:else}
    <QuestionSetCard
      {title}
      {description}
      {durationSeconds}
      questionCount={editable ? questions.length : questionCount}
      {responseCount}
      {editable}
      {typeLabel}
      {untitledText}
      {questionText}
      {questionsText}
      {responseText}
      {responsesText}
      {editLabel}
      {moveLabel}
      {duplicateLabel}
      {deleteLabel}
      isFirst={isFirstItem}
      isLast={isLastItem}
      moveTopLabel={moveToTopLabel}
      {moveUpLabel}
      {moveDownLabel}
      moveBottomLabel={moveToBottomLabel}
      on:open={() => (open = true)}
      on:opening={checkPlace}
      on:move={(event) => moveSet(event.detail)}
    />
  {/if}
</div>
